import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const UserIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const EnvelopeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const HeartIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

const AcademicCapIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const UsersIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const StaffCard = ({ name, role, image, emailUrl }) => (
  <div className="group flex flex-col items-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="relative mb-6">
      <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-4 border-school-gold/20 shadow-sm">
        {image ? (
          <img src={image} alt={name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-school-blue/8 text-school-blue">
            <UserIcon className="w-12 h-12" />
          </div>
        )}
      </div>
    </div>
    <h3 className="text-lg md:text-xl font-heading font-bold text-gray-900 mb-2 tracking-tight text-center group-hover:text-school-gold transition-colors">
      {name}
    </h3>
    <p className="text-sm md:text-base text-school-goldDark font-medium mb-4">{role}</p>
    {emailUrl && (
      <a
        href={emailUrl}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-school-blue/8 text-school-blue text-sm font-semibold hover:bg-school-blue hover:text-white transition-colors"
      >
        <EnvelopeIcon />
        Send Email
      </a>
    )}
  </div>
);

const ServiceCard = ({ icon, title, description }) => (
  <div className="flex items-start gap-5 p-7 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="flex-shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue shadow-sm">
      {icon}
    </div>
    <div className="flex-1">
      <h3 className="text-lg md:text-xl font-heading font-bold text-gray-900 mb-2 tracking-tight">
        {title}
      </h3>
      <p className="text-sm md:text-base text-gray-600 leading-7">{description}</p>
    </div>
  </div>
);

// ==================== MAIN COMPONENT ====================

const GuidanceCounseling = () => {
  const staffMembers = [
    {
      name: "Kristina Brannon",
      role: "Guidance Counselor",
      image: null,
      emailUrl: "/coming-soon"
    },
    {
      name: "Cristian Diaz",
      role: "Social Worker",
      image: "https://3.files.edl.io/8c6b/22/10/31/190858-fa2c4de6-6ac3-4507-b87d-0787de9ce397.jpg",
      emailUrl: "/coming-soon"
    },
    {
      name: "Catherine Espinal",
      role: "School Psychologist",
      image: null,
      emailUrl: "/coming-soon"
    },
    {
      name: "Emily Figueroa",
      role: "Family Worker",
      image: null,
      emailUrl: "/coming-soon"
    },
    {
      name: "Francesca Grosso",
      role: "Guidance Counselor",
      image: "https://3.files.edl.io/cbb4/21/11/18/125413-aab233f4-f832-47c9-857d-6fc19699525d.jpg",
      emailUrl: "/coming-soon"
    },
    {
      name: "Alexandra Luciano",
      role: "Family Worker",
      image: null,
      emailUrl: "/coming-soon"
    },
    {
      name: "John Matera",
      role: "School Psychologist",
      image: null,
      emailUrl: "/coming-soon"
    },
    {
      name: "Rosa Monegro",
      role: "Social Worker",
      image: null,
      emailUrl: "/coming-soon"
    },
    {
      name: "Jose Pagan",
      role: "STH Coordinator",
      image: null,
      emailUrl: "/coming-soon"
    },
    {
      name: "Nicole Reynolds",
      role: "School Counselor",
      image: null,
      emailUrl: "/coming-soon"
    },
    {
      name: "Esmeralda Rosario",
      role: "Social Worker",
      image: null,
      emailUrl: "/coming-soon"
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
                Student Services
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                Guidance & Counseling
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Supporting student success through comprehensive guidance and counseling services
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== INTRODUCTION ==================== */}
        <section className="py-20 md:py-28 bg-[#F5EFE0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Services"
              title="Comprehensive Student Support"
              description="Our dedicated team provides academic, social, and emotional support to help every student thrive"
            />
          </Container>
        </section>

        {/* ==================== SERVICES ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="What We Offer"
              title="Our Services"
              description="Supporting students and families through various counseling and guidance programs"
            />

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <ServiceCard
                icon={<AcademicCapIcon />}
                title="Academic Counseling"
                description="Supporting students in achieving their academic goals and reaching their full potential."
              />

              <ServiceCard
                icon={<HeartIcon />}
                title="Social-Emotional Support"
                description="Helping students develop healthy relationships and emotional well-being."
              />

              <ServiceCard
                icon={<UsersIcon />}
                title="Family Support"
                description="Working with families to ensure students receive comprehensive support at home and school."
              />
            </div>
          </Container>
        </section>

        {/* ==================== RESPECT FOR ALL ==================== */}
        <section className="py-20 md:py-28 bg-[#F5EFE0]" aria-labelledby="rfa-heading">
          <Container>
            <div className="max-w-4xl mx-auto">
              <figure>
                <img
                  src="/assets/images/respect-for-all.webp"
                  width={1780}
                  height={472}
                  alt="Kindness over bullies"
                  loading="lazy"
                  className="w-full h-auto rounded-3xl shadow-xl shadow-school-blue/10"
                />
              </figure>

              <div className="mt-12 text-center">
                <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
                  <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
                  Safe &amp; Supportive School
                </span>
                <h2
                  id="rfa-heading"
                  className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight"
                >
                  Respect for All
                </h2>
                <p className="mt-6 text-base md:text-lg text-gray-600 leading-8 text-left sm:text-center">
                  RFA is a NYCPS response to bullying and harassment. At PS 85 we are committed to keeping our
                  school safe, supportive and free from discrimination. During the month of February we celebrate
                  Respect for All Week to provide students with an opportunity to better understand diversity
                  around our school, city, and around the world.
                </p>
                <p className="mt-4 text-base md:text-lg text-gray-600 leading-8 text-left sm:text-center">
                  For more information about what the NYCPS are doing to ensure Respect for All, please visit the{' '}
                  <a
                    href="https://www.schools.nyc.gov/school-life/school-environment/respect-for-all"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-school-blue underline underline-offset-2 hover:text-school-goldDark focus:outline-none focus:ring-2 focus:ring-school-blue/40 rounded"
                  >
                    NYCPS Respect for All page<span className="sr-only"> (opens in new window)</span>
                  </a>
                  .
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== STAFF MEMBERS ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Team"
              title="Meet Our Staff"
              description="Dedicated professionals committed to student success and well-being"
            />

            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
              {staffMembers.map((staff, index) => (
                <StaffCard key={index} {...staff} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Need Support?
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Our guidance and counseling team is here to help. Reach out to us today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/coming-soon"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                Contact Us
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue font-medium border-2 border-school-blue/20 hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default GuidanceCounseling;
